import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import {
  enhancePresentationData,
  loadLucidState,
  loadPresentation,
  loadPresentationError,
  loadPresentationSuccess,
  loadSurroundingPresentationData,
  presentationModelElementDoubleClicked,
  presentationNodeDoubleClicked,
  renderMainPresentationView,
} from './actions';
import { toPresentationNode } from './toPresentationNode';
import { components$ } from 'streams/components/components$';
import { references$ } from 'streams/references/references$';
import { context$ } from 'streams/context/context$';
import { selectComponent } from 'streams/components/ComponentActions';
import {
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { isTruthy } from '@ardoq/common-helpers';
import { currentUserSettled$ } from './streams/currentUserSettled$';
import {
  currentUser$ as nullableCurrentUser$,
  isLoadedCurrentUser,
} from 'streams/currentUser/currentUser$';
import { featureContext$ } from 'streams/featureContext$';
import { getViewsToConvert } from './viewsToConvert';
import { loadMetamodels, setPresentationInContext } from './dataLoader';
import {
  loadSlides,
  selectNodeInPresentationMode,
  selectSlide,
} from './slideNavigator/actions';
import { trackViewedPresentation } from './tracking';
import { presentationOperations } from 'streams/presentations/presentationOperations';
import { logError } from '@ardoq/logging';
import {
  api,
  handleError,
  integrationsLucidChartApi,
  presentationApi,
  userApi,
} from '@ardoq/api';
import { renderToStaticMarkup } from 'react-dom/server';
import { ReactElement, createElement } from 'react';
import { mainView as ConnectedMainView } from './mainView';
import { deprecatedViews$ } from 'views/deprecatedViews$';
import { slideOperations } from 'streams/slides/slideOperations';
import { getNextSelectableSlideId } from './slideNavigator/slideSelection';
import {
  AccessErrorPresentation,
  NoOrgAccessErrorPresentation,
  PresentationLoadError,
  PresentationNotFound,
} from 'auth/AccessError';
import {
  applicationStarted,
  setReference,
} from 'streams/context/ContextActions';
import {
  APISlideAttributes,
  ArdoqId,
  PresentationReadPermissions,
} from '@ardoq/api-types';
import { createRoot } from 'react-dom/client';
import { isArdoqError } from '@ardoq/common-helpers';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import {
  fetchOrgUsers,
  fetchedOrgUsersSuccess,
  fetchOrgUsersError,
} from 'streams/orgUsers/actions';
import {
  addOrgParamToPresentationImagePaths,
  enhancePresentation,
  isOnlyContributor,
} from './utils';
import { loadCachedDynamicFilterResults } from './dynamicFilterCache';
import { loadWorkspaces, loadLucidSlides } from './loadUtil';
import { addFields } from 'streams/fields/actions';
import { loadedPresentation$ } from './streams/loadedPresentation$';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { LOCALE_BUNDLES } from 'localeResources';
import {
  resolveErrorScreenI18nLocale,
  resolvePreStreamI18nLocale,
} from 'streams/translationSync';
import { confidentialFieldsReceived } from 'streams/currentUserPermissions/actions';
import {
  validateUserSessionAndRedirectIfNeeded,
  Redirect,
} from 'authentication';
import { setLucidSlideState } from 'integrations/lucidchart/actions';
import { addModels } from 'streams/models/actions';
import { t } from '@ardoq/i18n';

const bsonIdRegExp = new RegExp('^[0-9a-fA-F]{24}$');
const handleLoadPresentation = routine(
  ofType(loadPresentation),
  extractPayload(),
  switchMap(payload =>
    currentUserSettled$.pipe(
      filter(isTruthy),
      take(1),
      withLatestFrom(nullableCurrentUser$, featureContext$),
      map(([, user, featureContext]) => ({
        ...payload,
        viewsToConvert: getViewsToConvert(
          isLoadedCurrentUser(user),
          featureContext
        ),
      }))
    )
  ),
  switchMap(({ presentationId, contextComponentId, viewsToConvert }) => {
    if (!bsonIdRegExp.test(presentationId)) {
      dispatchAction(
        loadPresentationError({
          error: 'Invalid presentation id provided',
        })
      );
    }
    const validContextComponentId =
      contextComponentId && bsonIdRegExp.test(contextComponentId)
        ? contextComponentId
        : undefined;
    return presentationApi.fetchAggregated(
      presentationId,
      validContextComponentId ?? null,
      viewsToConvert
    );
  }),
  handleError(async error => {
    if (api.isUnauthorized(error)) {
      const user = await validateUserSessionAndRedirectIfNeeded(Redirect.LOGIN);
      if (!user) {
        // We have been redirected
        return;
      }
    }
    api.logErrorIfNeeded(error);
    return dispatchAction(
      loadPresentationError({
        error,
      })
    );
  }),
  map(enhancePresentationData),
  tap(dispatchAction)
);

const handleEnhancePresentation = routine(
  ofType(enhancePresentationData),
  extractPayload(),
  withLatestFrom(loadedPresentation$),
  map(([presentation, { organizationLabel, isCustomDomain }]) => {
    if (isCustomDomain) return presentation;
    if (presentation.readAccess !== PresentationReadPermissions.ALL)
      return presentation;
    return addOrgParamToPresentationImagePaths(presentation, organizationLabel);
  }),
  map(presentation => enhancePresentation(presentation)),
  map(loadSurroundingPresentationData),
  tap(dispatchAction)
);

const handleLoadUserForPresentation = routine(
  ofType(enhancePresentationData),
  withLatestFrom(currentUser$),
  tap(async ([, user]) => {
    const orgLabel = user.organization?.label;

    // In cases where there is no "current org", do not fetch user list either.
    if (!orgLabel) return;
    if (isOnlyContributor(user, orgLabel)) return;

    dispatchActionAndWaitForResponse(
      fetchOrgUsers(),
      fetchedOrgUsersSuccess,
      fetchOrgUsersError
    );
  })
);

const mountMainPresentationView = (): void => {
  const root = createRoot(document.querySelector('#root')!);
  root.render(createElement(ConnectedMainView));
  dispatchAction(renderMainPresentationView());
  dispatchAction(applicationStarted());
};

const handleLoadSurroundingPresentationData = routine(
  ofType(loadSurroundingPresentationData),
  extractPayload(),
  withLatestFrom(loadedPresentation$, deprecatedViews$),
  tap(
    ([
      presentation,
      { loadedSlideId, presentation: previous },
      deprecatedViews,
    ]) => {
      const isViewerMounted = previous !== null;
      dispatchAction(
        confidentialFieldsReceived(presentation.fieldAccessContext)
      );
      dispatchAction(addModels(presentation.models));
      dispatchAction(
        addFields({
          fields: presentation.fields,
          options: { silent: true, sort: true },
        })
      );
      loadWorkspaces(presentation.workspaces);
      loadCachedDynamicFilterResults(presentation.dynamicFilterResults || []);
      dispatchAction(loadSlides(presentation.authorizedSlides));
      loadMetamodels(presentation.metamodels, presentation.fields);
      loadLucidSlides();

      // i18nLocale$.state OK here: imperative DOM document.title setter inside a
      // routine that runs on each presentation load.
      const i18nLocale = i18nLocale$.state;
      const presentationName =
        presentation.name ||
        t(
          i18nLocale,
          'presentationAdmin:viewer.defaultPresentationName',
          'Presentation'
        );
      document.title = t(
        i18nLocale,
        'presentationAdmin:viewer.documentTitle',
        { name: presentationName },
        'Ardoq - {{name}}'
      );
      // slidesById mirrors the same objects dispatched to slides$ via loadSlides
      // above; the two representations can diverge if slides$ is updated (e.g. by
      // a websocket event) between that dispatch and a later slides$ read.
      const slidesById: Record<ArdoqId, APISlideAttributes> =
        Object.fromEntries(
          presentation.authorizedSlides.map(slide => [slide._id, slide])
        );
      setPresentationInContext(presentation, deprecatedViews);
      dispatchAction(loadPresentationSuccess(presentation));
      if (!isViewerMounted) mountMainPresentationView();
      const slides = presentation.slides ?? [];

      const slideToSelect =
        (loadedSlideId && slides.includes(loadedSlideId) && loadedSlideId) ||
        getNextSelectableSlideId({
          slides: slides.map(id => ({
            id,
            viewId:
              slidesById[id] && slideOperations.getSlideViewId(slidesById[id]),
          })),
          selectedSlideId: null,
          deprecatedViews,
        }) ||
        slides[0];

      dispatchAction(selectSlide(slideToSelect || null));
      trackViewedPresentation({
        presentationId: presentation._id,
        ...presentationOperations.getSolutionMetadata(presentation),
      });
    }
  )
);

const renderErrorPage = (element: ReactElement) => {
  const html = renderToStaticMarkup(element);
  document.body.insertAdjacentHTML('afterbegin', html);
  document.querySelector('#root')?.remove();
};

const showPresentationLoadError = async () => {
  const i18nLocale = await resolveErrorScreenI18nLocale(LOCALE_BUNDLES);
  renderErrorPage(createElement(PresentationLoadError, { i18nLocale }));
};

// Please don't use the following code for inspiration. It's just wrong.
const handleLoadPresentationError = routine(
  ofType(loadPresentationError),
  extractPayload(),
  tap(async ({ error }) => {
    if (api.isUnauthorized(error)) {
      const user = await userApi.fetchCurrentUser();
      if (isArdoqError(user)) {
        logError(error);
        await showPresentationLoadError();
        return;
      }
      const i18nLocale = await resolvePreStreamI18nLocale(user, LOCALE_BUNDLES);
      renderErrorPage(
        createElement(AccessErrorPresentation, {
          user,
          i18nLocale,
        })
      );
    } else if (api.isForbidden(error)) {
      const i18nLocale = await resolveErrorScreenI18nLocale(LOCALE_BUNDLES);
      renderErrorPage(
        createElement(NoOrgAccessErrorPresentation, { i18nLocale })
      );
    } else if (api.isNotFound(error)) {
      const i18nLocale = await resolveErrorScreenI18nLocale(LOCALE_BUNDLES);
      renderErrorPage(createElement(PresentationNotFound, { i18nLocale }));
    } else {
      logError(error);
      await showPresentationLoadError();
    }
  })
);

export const handleLoadLucidState = routine(
  ofType(loadLucidState),
  switchMap(integrationsLucidChartApi.settings),
  handleError(),
  tap(response => {
    dispatchAction(
      setLucidSlideState({
        clientId: response.clientId,
      })
    );
  })
);

const handlePresentationNodeDoubleClicked = routine(
  ofType(presentationNodeDoubleClicked),
  extractPayload(),
  withLatestFrom(context$),
  tap(([{ nodeId, entityType }, { isExploreMode }]) => {
    if (!isExploreMode) return;
    if (entityType === 'component') {
      dispatchAction(selectComponent({ cid: nodeId }));
      return;
    }
    dispatchAction(setReference(nodeId));
  })
);

const handlePresentationModelElementDoubleClicked = routine(
  ofType(presentationModelElementDoubleClicked),
  extractPayload(),
  withLatestFrom(components$, references$),
  map(([payload, components, references]) =>
    toPresentationNode(payload, components, references)
  ),
  filter(isTruthy),
  tap(({ id, entityType }) => {
    dispatchAction(selectNodeInPresentationMode(id));
    dispatchAction(presentationNodeDoubleClicked({ nodeId: id, entityType }));
  })
);

export const routines = collectRoutines(
  handleLoadPresentation,
  handleLoadPresentationError,
  handleLoadUserForPresentation,
  handleEnhancePresentation,
  handleLoadSurroundingPresentationData,
  handleLoadLucidState,
  handlePresentationNodeDoubleClicked,
  handlePresentationModelElementDoubleClicked
);
